
import * as React from "react";
import { X, Filter, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface TagInputProps {
  selected: string[];
  options: { label: string; value: string }[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  creatable?: boolean;
}


export function TagInput({
  selected = [],
  options = [],
  onChange,
  placeholder = "Add tags...",
  creatable = false,
}: TagInputProps) {
  const [inputValue, setInputValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue) {
      e.preventDefault();
      if (creatable || options.find(opt => opt.value === inputValue)) {
        onChange([...selected, inputValue]);
        setInputValue("");
        setIsOpen(false);
      }
    }
  };


  const handleRemoveTag = (tagToRemove: string) => {
    onChange(selected.filter(tag => tag !== tagToRemove));
  };


  const filteredOptions = options.filter(option => 
    !selected.includes(option.value) &&
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              "h-9 justify-start gap-2 bg-background hover:bg-accent transition-colors",
              selected.length > 0 && "border-primary"
            )}
          >
            <Filter className="h-4 w-4" />
            <span className="flex-1 text-left">
              {selected.length > 0 ? `${placeholder} (${selected.length})` : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[320px] p-0 z-[100] bg-popover border shadow-md" 
          align="start"
          sideOffset={8}
        >
          {selected.length > 0 && (
            <div className="p-3 border-b bg-muted/50">
              <div className="flex flex-wrap gap-1.5">
                {selected.map(tag => (
                  <Badge key={tag} variant="secondary" className="gap-1 text-xs">
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="p-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type to search or create..."
              className="h-9 border-0 focus-visible:ring-0 bg-transparent"
            />
          </div>
          <div className="max-h-[200px] overflow-auto border-t">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([...selected, option.value]);
                    setInputValue("");
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                >
                  {option.label}
                </button>
              ))
            ) : creatable && inputValue ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([...selected, inputValue]);
                  setInputValue("");
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent transition-colors text-primary"
              >
                Create "{inputValue}"
              </button>
            ) : (
              <p className="px-3 py-2 text-sm text-muted-foreground text-center">
                No options found
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
